import { defineStore } from "pinia";
import type { FetchError } from "ofetch";
import { EntityTypeEnum } from "~~/enums/EntityType";
import { ProspectPageEnum } from "~~/enums/ProspectPage";
import Discussion from "~~/models/Discussion";
import ErrorModel from "~~/models/ErrorModel";
import Message from "~~/models/Message";
import MessagesStatusModel from "~~/models/MessagesStatusModel";
import Discussions from "~~/models/Discussions";
import CurrentPage from "~~/models/CurrentPage";
import { DocumentProcessingMode } from "~~/enums/DocumentProcessingMode";

export const useProspectStore = defineStore({
  id: "prospect-store",
  state: () => {
    return {
      currentProspect: null,
      currentProspectType: EntityTypeEnum.Individual,
      prospectList: null as Ref<Array<any>> | null,
      attachment: null as Blob | null,
      attachmentId: null,
      newMessages: null as Array<Message> | null,
      discussion: null as Discussion | null,
      discussions: null as Discussions | null,
      prospectCurrentPage: null,
      openingSet: null as Blob | null,
      prospectManager: null,
      isLoading: false,
      isLoadingAttachment: false,
      isLoadingDiscussion: false,
      displayRegistration: false,
    };
  },
  actions: {
    // Loading prospect from Api
    async loadProspect(prospectId: string) {
      this.isLoading = true;
      let { data, error } = await useFetch<any, FetchError<ErrorModel>>(
        `${useBaseUrl()}/api/Prospect?ProspectId=${prospectId}`,
        {
          headers: await useUser().getHeadersWithToken(),
          initialCache: false,
        }
      );
      this.isLoading = false;

      if (error && error.value && error.value.response) {
        throw new Error(error.value.response._data?.errorCode);
      }

      this.currentProspect = data.value;
    },
    async loadProspectList() {
      let { data } = await useFetch<any>(useBaseUrl() + `/api/Prospect/List`, {
        headers: await useUser().getHeadersWithToken(),
        method: "GET",
        initialCache: false,
      });
      this.prospectList = data.value;
    },
    setProspectType(entityType: EntityTypeEnum) {
      this.currentProspectType = entityType;
    },

    // Saving prospect to Api
    async addIndividual(payload: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/AddIndividual",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(payload),
          }
        );
        this.isLoading = false;

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while creating the individual prospect");
        }
      } catch {
        this.isLoading = false;
        throw new Error("Error while creating the individual prospect");
      }
    },
    async addCorporate(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/AddCorporate",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );
        this.isLoading = false;

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while creating the corporate prospect");
        }
      } catch {
        this.isLoading = false;
        throw new Error("Error while creating the corporate prospect");
      }
    },

    // File upload & download
    async addAttachment(file: any) {
      this.attachmentId = null;
      const formData = new FormData();
      formData.append("file", file);

      let { data, error } = await useFetch<any>(
        useBaseUrl() + `/api/Prospect/AddAttachment`,
        {
          headers: {
            ...(await useUser().getHeadersWithToken()),
          },
          method: "POST",
          body: formData,
          initialCache: false,
          credentials: "include",
        }
      );

      if (error && error.value && error.value.response) {
        throw new Error(
          error.value.response._data?.errors[""].length > 0
            ? "attachmentTooBig"
            : error.value.response._data?.errorCode
        );
      }

      this.attachmentId = data.value.attachmentId;
    },

    async loadAttachment(attachmentId: string) {
      let { data, error } = await useFetch<Blob, FetchError<ErrorModel>>(
        `${useBaseUrl()}/api/Prospect/GetAttachment?AttachmentId=${attachmentId}`,
        {
          headers: await useUser().getHeadersWithToken(),
          method: "GET",
          initialCache: false,
        }
      );

      if (error && error.value && error.value.response) {
        throw new Error(error.value.response._data?.errorCode);
      }

      this.attachment = data.value as Blob;
    },

    // Prospect current page
    async getProspectCurrentPage(prospectId: any): CurrentPage {
      try {
        this.isLoading = true;
        let { data, error } = await useFetch<CurrentPage>(
          useBaseUrl() +
            `/api/Prospect/GetProspectCurrentPage?ProspectId=${prospectId}`,
          {
            headers: await useUser().getHeadersWithToken(),
            method: "GET",
            initialCache: false,
          }
        );
        this.isLoading = false;

        if (error && error.value && error.value.response) {
          throw new Error(error.value.response._data?.errorCode);
        }

        return data.value as CurrentPage;
      } catch {
        this.isLoading = false;
        throw new Error("Error while retrieving the current prospect page");
      }
    },

    //Prospect page access
    async getPageAccessForProspectId(
      prospectId: any,
      currentPage: ProspectPageEnum
    ): Promise<boolean> {
      let { data, error } = await useFetch<any>(
        useBaseUrl() +
          `/api/Prospect/getPageAccessForProspectId?ProspectId=${prospectId}&currentPage=${currentPage}`,
        {
          headers: await useUser().getHeadersWithToken(),
          method: "GET",
          initialCache: false,
        }
      );

      if (error && error.value && error.value.response) {
        throw new Error(error.value.response._data?.errorCode);
      }

      return data?.value?.hasAccess;
    },

    // Register individual prospect
    async saveIndividualLandingPage(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveIndividualLandingPage",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error(
            "Error while saving the individual personal information"
          );
        }
      } catch {
        throw new Error(
          "Error while saving the individual personal information"
        );
      } finally {
        this.isLoading = false;
      }
    },
    // Register corporate prospect
    async saveCorporateLandingPage(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveCorporateLandingPage",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the corporate information");
        }
      } catch {
        throw new Error("Error while saving the corporate information");
      } finally {
        this.isLoading = false;
      }
    },
    // Register save individual registration
    async saveIndividualRegistration(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveIndividualRegistration",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the individual registration");
        }
      } catch {
        throw new Error("Error while saving the individual registration");
      } finally {
        this.isLoading = false;
      }
    },
    async saveIndividualPreKyc(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveIndividualPreKyc",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the individual pre KYC");
        }
      } catch {
        throw new Error("Error while saving the individual pre KYC");
      } finally {
        this.isLoading = false;
      }
    },
    async SaveIndividualTermsAndConditions(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveIndividualTermsAndConditions",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error(
            "Error while saving the individual terms & conditions"
          );
        }
      } catch {
        throw new Error("Error while saving the individual terms & conditions");
      } finally {
        this.isLoading = false;
      }
    },
    async saveIndividualToInProgress(prospect: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/saveIndividualToInProgress",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error(
            "Error while changing the prospect to In Progress status"
          );
        }
      } catch {
        throw new Error(
          "Error while changing the prospect to In Progress status"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async saveIndividualDocuments(docs: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveIndividualDocuments",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(docs),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error(
            "Error while saving the individual prospect documents"
          );
        }
      } catch {
        throw new Error("Error while saving the individual prospect documents");
      } finally {
        this.isLoading = false;
      }
    },
    async downloadOpeningSet(
      prospectId: string,
      includesDefaults: boolean,
      isReadOnly: boolean,
      isEsignature: boolean,
      languageIsoCode: string,
      templateSelectionList: Array<string> = []
    ) {
      this.isLoading = true;
      let { data, error } = await useFetch<any>(
        useBaseUrl() + `/api/Prospect/GetOpeningSetByProspectId`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(await useUser().getHeadersWithToken()),
          },
          method: "POST",
          body: JSON.stringify({
            prospectId: prospectId,
            includesDefaults: includesDefaults,
            languageIsoCode: languageIsoCode,
            documentProcessingMode: isReadOnly
              ? DocumentProcessingMode.Draft
              : DocumentProcessingMode.ReadWrite,
            isEsignature: isEsignature,
            templateSelectionList: templateSelectionList,
          }),
          initialCache: false,
          credentials: "include",
        }
      );
      this.isLoading = false;

      if (error && error.value && error.value.response) {
        throw new Error(error.value.response._data.errorCode);
      }

      this.openingSet = data;
    },
    async getProspectManager(prospect: any) {
      let payload = "";
      if (prospect.prospectId) {
        payload = `?prospectId=${prospect.prospectId}`;
      }

      if (prospect.prospectType != null) {
        payload = `?prospectType=${prospect.prospectType}`;
      }

      let { data } = await useFetch<any>(
        useBaseUrl() + prospect.prospectId
          ? `/api/Prospect/GetProspectManager${payload}`
          : `/api/Prospect/GetManager${payload}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(await useUser().getHeadersWithToken()),
          },
        }
      );
      return data?.value;
    },
    async saveCorporateRegistration(payload: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveCorporateRegistration",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the corporate prospect");
        }
      } catch {
        throw new Error("Error while saving the corporate prospect");
      } finally {
        this.isLoading = false;
      }
    },
    async saveCorporateIndividual(payload: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveCorporateIndividual",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the corporate prospect");
        }
      } catch {
        throw new Error("Error while saving the corporate prospect");
      } finally {
        this.isLoading = false;
      }
    },
    async deleteCorporateIndividual(payload: any) {
      try {
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/DeleteCorporateIndividual",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while deleting the corporate prospect");
        }
      } catch (e) {
        throw new Error("Error while deleting the corporate prospect");
      } finally {
        this.isLoading = false;
      }
    },
    async saveCorporateTransaction(payload: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveCorporateTransaction",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the corporate transaction");
        }
      } catch {
        throw new Error("Error while saving the corporate transaction");
      } finally {
        this.isLoading = false;
      }
    },
    async loadDiscussions(prospectId: string) {
      this.isLoadingDiscussion = true;
      let { data, error } = await useFetch<Discussions, FetchError<ErrorModel>>(
        useBaseUrl() + `/api/Prospect/GetDiscussions?ProspectId=${prospectId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(await useUser().getHeadersWithToken()),
          },
        }
      );
      this.isLoadingDiscussion = false;

      if (error?.value?.response && error.value.response.status != 400) {
        throw new Error(error.value.response._data?.errorCode);
      }

      this.discussions = data?.value;
    },
    async loadDiscussion(discussionId?: string, prospectId?: string) {
      this.isLoadingDiscussion = true;

      let payload = discussionId
        ? `DiscussionId=${discussionId}`
        : `ProspectId=${prospectId}`;

      let { data, error } = await useFetch<Discussion, FetchError<ErrorModel>>(
        useBaseUrl() + `/api/Prospect/GetDiscussion?${payload}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(await useUser().getHeadersWithToken()),
          },
        }
      );
      this.isLoadingDiscussion = false;

      if (error?.value?.response && error.value.response.status != 400) {
        throw new Error(error.value.response._data?.errorCode);
      }

      this.discussion = data?.value;
    },
    async addMessage(
      prospectId: string,
      message: string,
      attachmentIds: Array<string>
    ): Promise<boolean> {
      this.isLoadingDiscussion = true;
      let { data, error } = await useFetch<any, FetchError<ErrorModel>>(
        useBaseUrl() + `/api/Prospect/AddMessage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(await useUser().getHeadersWithToken()),
          },
          body: JSON.stringify({
            prospectId: prospectId,
            message: message,
            attachmentIdList: attachmentIds,
          }),
          initialCache: false,
          credentials: "include",
        }
      );
      this.isLoadingDiscussion = false;

      if (error && error.value && error.value.response) {
        throw new Error(error.value.response._data?.errorCode);
      }

      return data?.value?.isSuccess;
    },
    async updateMessageStatus(
      messageIds: Array<string>
    ): Promise<MessagesStatusModel> {
      let { data, error } = await useFetch<
        Ref<MessagesStatusModel>,
        FetchError<ErrorModel>
      >(useBaseUrl() + `/api/Prospect/UpdateMessageStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await useUser().getHeadersWithToken()),
        },
        body: JSON.stringify({
          messageIdList: messageIds,
        }),
        initialCache: false,
        credentials: "include",
      });

      if (error && error.value && error.value.response) {
        throw new Error(error.value.response._data?.errorCode);
      }

      return data?.value as MessagesStatusModel;
    },

    async saveProspectCounterparty(prospect: Object) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveProspectCounterparty",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while saving the prospect conterparty");
        }
      } catch {
        throw new Error("Error while saving the prospect conterparty");
      } finally {
        this.isLoading = false;
      }
    },
    async saveCorporateUnderReview(prospect: Object) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/SaveCorporateUnderReview",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(prospect),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error(
            "Error while changing the prospect to Under Review status"
          );
        }
      } catch {
        throw new Error(
          "Error while changing the prospect to Under Review status"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async deleteProspectCounterparty(entity: any) {
      try {
        this.isLoading = true;
        const response = await fetch(
          useBaseUrl() + "/api/Prospect/DeleteProspectCounterparty",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await useUser().getHeadersWithToken()),
            },
            body: JSON.stringify(entity),
          }
        );

        if (response.ok) {
          return response;
        } else {
          throw new Error("Error while deleting the prospect counterparty");
        }
      } catch {
        throw new Error("Error while deleting the prospect counterparty");
      } finally {
        this.isLoading = false;
      }
    },
    showRegistration() {
      this.displayRegistration = true;
    },
    hideRegistration() {
      this.displayRegistration = false;
    },
    clear() {
      this.currentProspect = null;
      this.currentProspectType = EntityTypeEnum.Individual;
      this.prospectList = null;
      this.attachment = null;
      this.attachmentId = null;
      this.newMessages = null;
      this.discussion = null;
      this.discussions = null;
      this.prospectCurrentPage = null;
      this.openingSet = null;
      this.prospectManager = null;
      this.displayRegistration = false;
    },
  },
  getters: {
    getProspect: (state) => state.currentProspect,
    getProspectType: (state) => state.currentProspectType,
    getProspectList: (state) => state.prospectList,
    getAttachmentId: (state) => state.attachmentId,
    getAttachment: (state) => state.attachment,
    getProspectDiscussions: (state) => state.discussions,
    getProspectDiscussion: (state) => state.discussion,
    getOpeningSet: (state) => state.openingSet,
    getIsLoading: (state) => state.isLoading,
    getDisplayRegistrations: (state) => state.displayRegistration,
  },
});
